import type { TBConsumeFunction, TBElement } from '@ttab/textbit'
import type { Block } from '@ttab/elephant-api/newsdoc'
import type { Repository } from '@/shared/Repository'
import { getCachedSession } from '@/shared/getCachedSession'
import { transformFactbox } from '@/shared/transformations/newsdoc/core/factbox'
import { toast } from 'sonner'
import i18next from 'i18next'

type FactboxDragPayload = {
  id: string
  title: string
  text: string
  modified?: string
  original_version?: string
  original_updated?: string
  locally_changed?: string
  created?: string
}

const EMBEDDED_ID_RE = /^(.+):embedded:(\d+)$/

const fallbackChildren = (payload: FactboxDragPayload): TBElement[] => [
  {
    id: crypto.randomUUID(),
    class: 'text',
    type: 'core/factbox/title',
    children: [{ text: payload.title }]
  },
  {
    id: crypto.randomUUID(),
    class: 'block',
    type: 'core/factbox/body',
    children: payload.text.split('\n').map((t) => ({
      id: crypto.randomUUID(),
      type: 'core/text',
      class: 'text',
      children: [{ text: t }]
    }))
  }
]

/**
 * Returns the core/factbox Block for a dragged factbox, either the full
 * document (standalone factboxes) or the nth core/factbox block inside
 * an article (embedded factboxes). Returns undefined if the document
 * exists but the requested block is missing; throws if the fetch fails
 * so the caller can refuse the insert and surface the error.
 */
const fetchFactboxBlock = async (
  payload: FactboxDragPayload,
  repository: Repository,
  accessToken: string
): Promise<Block | undefined> => {
  const embedded = EMBEDDED_ID_RE.exec(payload.id)

  if (embedded) {
    const [, articleId, indexStr] = embedded
    const response = await repository.getDocument({ uuid: articleId, accessToken })

    const block = response?.document?.content
      .filter((b) => b.type === 'core/factbox')[parseInt(indexStr)]

    return block
  }

  const response = await repository.getDocument({ uuid: payload.id, accessToken })

  if (!response?.document) {
    return undefined
  }

  const { document } = response

  return {
    id: document.uuid,
    uuid: document.uuid,
    uri: document.uri,
    url: document.url,
    type: document.type,
    title: document.title,
    data: {},
    content: document.content,
    meta: document.meta,
    links: document.links,
    contenttype: '',
    name: '',
    rel: '',
    role: '',
    value: '',
    sensitivity: ''
  }
}

export const createFactboxConsume = (
  repository: Repository | undefined
): TBConsumeFunction => async ({ input }) => {
  if (Array.isArray(input)) {
    throw new Error('Factbox plugin expected string for consumation, not a list/array')
  }

  if (typeof input.data !== 'string') {
    throw new Error('Factbox plugin expected string for consumation')
  }

  const payload = JSON.parse(input.data) as FactboxDragPayload
  const isEmbedded = EMBEDDED_ID_RE.test(payload.id)

  let block: Block | undefined
  let children: TBElement[] | undefined

  if (repository) {
    try {
      const session = await getCachedSession()
      if (session?.accessToken) {
        block = await fetchFactboxBlock(payload, repository, session.accessToken)
      }
    } catch (error) {
      console.error('Failed to fetch factbox for drag-drop:', error)
      toast.error(i18next.t('errors:toasts.couldNotLoadFactbox'))
      return undefined
    }

    if (block) {
      const transformed = transformFactbox(block)
      children = transformed.children as TBElement[]
    }
  }

  if (!children) {
    children = fallbackChildren(payload)
  }

  // Embedded payload.id is "articleId:embedded:N" - not a valid UUID for a source link.
  const sourceId = isEmbedded
    ? block?.links?.find((l) => l.rel === 'source')?.uuid
    : payload.id

  return {
    ...input,
    data: {
      id: crypto.randomUUID(),
      class: 'block',
      type: 'core/factbox',
      properties: {
        title: payload.title,
        text: payload.text,
        created: payload.created,
        modified: payload.modified,
        id: sourceId,
        original_id: sourceId,
        original_updated: payload.original_updated,
        original_version: payload.original_version,
        locally_changed: payload.locally_changed,
        rel: 'factbox',
        type: 'core/factbox'
      },
      children
    }
  }
}
